import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import Button from '@atlaskit/button';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import { MAX_SCROLL_WIDTH } from '../../../constants/widget-container';
import './widget-container.scss';

type Children = React.ReactNode;

const checkArrowsVisibility = (scrollEl: HTMLElement) => {
  const scrollLeft = scrollEl.scrollLeft;
  const scrollWidth = scrollEl.scrollWidth;
  const clientWidth = scrollEl.clientWidth;
  const isLeftArrowVisible = scrollLeft > 0;
  const isRightArrowVisible = Math.abs(scrollWidth - scrollLeft - clientWidth) > 1;
  return {
    isLeftArrowVisible,
    isRightArrowVisible,
  };
};

const scrollWidgetContainer = (scrollEl: HTMLElement, toScrollRight: boolean) => {
  const scrollBy = toScrollRight ? MAX_SCROLL_WIDTH : -MAX_SCROLL_WIDTH;
  scrollEl.scrollBy({
    left: scrollBy,
    behavior: 'smooth',
  });
};

export const WidgetContainer = (props: { heading: string; children?: Children }) => {
  const widgetSliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setLeftArrowVisibility] = useState<boolean>(false);
  const [showRightArrow, setRightArrowVisibility] = useState<boolean>(true);

  const checkArrowVisibility = useCallback(() => {
    const el = widgetSliderRef.current;
    if (!el) {
      return;
    }
    const { isRightArrowVisible, isLeftArrowVisible } = checkArrowsVisibility(el);
    setLeftArrowVisibility(isLeftArrowVisible);
    setRightArrowVisibility(isRightArrowVisible);
  }, [widgetSliderRef]);

  const onArrowClick = useCallback(
    (isRightArrow = true) => {
      const el = widgetSliderRef.current;
      if (!el) {
        return;
      }
      scrollWidgetContainer(el, isRightArrow);
    },
    [widgetSliderRef]
  );

  const onWidgetContainerScroll = useMemo(() => debounce(checkArrowVisibility, 100), [widgetSliderRef]);

  useEffect(checkArrowVisibility, [widgetSliderRef, checkArrowVisibility]);

  const renderWidgets = (children: Children) => {
    if (!children) {
      return null;
    }

    return React.Children.map(children, (child: React.ReactNode, index: number) => (
      <div className="widget-slider-item" data-testid={`widget-slider-item-${index}`} key={index}>
        {child}
      </div>
    ));
  };

  return (
    <section className="ppc-widget-container">
      <div className="container">
        <h2 className="widget-container-heading mb-24">{props.heading}</h2>
      </div>
      <div className="widget-slider" onScroll={onWidgetContainerScroll} ref={widgetSliderRef}>
        <div className="widget-slider-content">{renderWidgets(props.children)}</div>
      </div>
      <div className="widget-slider-navigation">
        <div className={classnames('widget-slider-nav-btn widget-slider-nav-btn-left', { visible: showLeftArrow })}>
          <Button
            onClick={onArrowClick.bind(null, false)}
            appearance="subtle"
            iconBefore={<ArrowLeftCircleIcon label="left-nav-arrow" size="xlarge" />}
            className="widget-slider-arrow widget-slider-arrow-left"
          />
        </div>
        <div className={classnames('widget-slider-nav-btn widget-slider-nav-btn-right', { visible: showRightArrow })}>
          <Button
            onClick={onArrowClick}
            appearance="subtle"
            iconBefore={<ArrowLeftCircleIcon label="right-nav-arrow" size="xlarge" />}
            className="widget-slider-arrow widget-slider-arrow-right"
          />
        </div>
      </div>
    </section>
  );
};
